import { useEffect, useState } from 'react'

import {
  useDeactivateWebPushDeviceMutation,
  useLazyGetWebPushRegistrationStatusQuery,
  useRegisterWebPushDeviceMutation,
  useUpdateWebPushPreferenceMutation,
} from '../api/web-push.api'
import { getBrowserInstallationId } from '../utils/getBrowserInstallationId'

export const useInitialNotificationReconciliation = (
  membershipId: string | undefined,
) => {
  const [invitation, setInvitation] = useState<{
    membershipId: string
    browserInstallationId: string
    permissionIssue?: 'denied' | 'default'
  } | null>(null)
  const [getStatus] = useLazyGetWebPushRegistrationStatusQuery()
  const [registerDevice] = useRegisterWebPushDeviceMutation()
  const [deactivateDevice] = useDeactivateWebPushDeviceMutation()
  const [updatePreference] = useUpdateWebPushPreferenceMutation()

  useEffect(() => {
    if (!membershipId || typeof Notification === 'undefined') return
    let cancelled = false
    let statusRequest: ReturnType<typeof getStatus> | undefined
    let mutation: { abort: () => void } | undefined

    const reconcile = async () => {
      const browserInstallationId = await getBrowserInstallationId()
      if (cancelled) return
      const context = { membershipId, browserInstallationId }
      statusRequest = getStatus(context, false)
      const status = await statusRequest.unwrap().finally(() => {
        statusRequest?.unsubscribe()
      })
      if (cancelled) return

      if (Notification.permission === 'denied') {
        setInvitation(
          status.notificationsEnabled
            ? { ...context, permissionIssue: 'denied' }
            : null,
        )
        if (
          !status.registered ||
          status.notificationsEnabled ||
          status.active
        ) {
          const request = updatePreference({
            ...context,
            notificationsEnabled: false,
          })
          mutation = request
          await request.unwrap()
        }
        return
      }

      if (Notification.permission === 'default') {
        if (status.active) {
          const request = deactivateDevice(context)
          mutation = request
          await request.unwrap()
        }
        if (!cancelled && (!status.registered || status.notificationsEnabled)) {
          setInvitation(
            status.notificationsEnabled
              ? { ...context, permissionIssue: 'default' }
              : context,
          )
        } else if (!cancelled) {
          setInvitation(null)
        }
        return
      }

      if (!status.registered) {
        setInvitation(context)
        return
      }
      setInvitation(null)
      if (!status.notificationsEnabled) return

      const { getWebPushToken } = await import('../utils/getWebPushToken')
      if (cancelled) return
      const token = await getWebPushToken()
      if (cancelled || Notification.permission !== 'granted') return

      const request = registerDevice({ ...context, token })
      mutation = request
      await request.unwrap()
    }

    queueMicrotask(() => {
      if (cancelled) return
      void reconcile().catch(() => {
        if (!cancelled)
          console.warn('Initial notification reconciliation failed.')
      })
    })

    return () => {
      cancelled = true
      statusRequest?.unsubscribe()
      statusRequest?.abort()
      mutation?.abort()
    }
  }, [
    membershipId,
    getStatus,
    registerDevice,
    deactivateDevice,
    updatePreference,
  ])

  return invitation?.membershipId === membershipId ? invitation : null
}
